import { useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { entityApi, pipelineNotificationApi } from '@/modules/pipelines/api';
import type { PipelineNode } from '@/modules/pipelines/api/entity-api';
import PipelineMap from '@/modules/pipelines/components/deep-dive/map/pipeline-map';
import MapFilterDropdown from '@/modules/pipelines/components/deep-dive/map/map-filter-dropdown';
import styles from '@/modules/pipelines/components/deep-dive/pipeline-map-tabs.module.scss';
import {
  collectMapEntityKeys,
  pipelineGraphToGeoJson,
  pipelineGraphToZonesGeoJson,
} from '@/modules/pipelines/data/pipelines';
import { emptyRegionsGeoJson, entitiesToRegionsGeoJson } from '@/modules/pipelines/data/regions';
import {
  MapCamera,
  MapUrlState,
  queryOfWidgetPath,
  readMapUrlState,
} from '@/modules/pipelines/data/map-url-state';
import useCopyLinkAction from '@/modules/pipelines/hooks/use-copy-link-action';
import { LocalWidgetRouterContext } from '@/core/widgets/export';
import { Col, Row } from 'react-bootstrap';

const ZONE_FOCUS_ZOOM = 7;

export default function PipelineMapPanel() {
  const localRouter = useContext(LocalWidgetRouterContext);
  const [initialUrlState] = useState(() =>
    readMapUrlState(new URLSearchParams(queryOfWidgetPath(localRouter?.path))),
  );

  const [regions, setRegions] = useState(emptyRegionsGeoJson);
  const [pipelineNodes, setPipelineNodes] = useState<PipelineNode[]>([]);
  const [noticesByEntity, setNoticesByEntity] = useState<Map<string, unknown[]>>(new Map());
  const [selectedRegions, setSelectedRegions] = useState<string[]>(initialUrlState.regions);
  const [selectedPipelines, setSelectedPipelines] = useState<string[]>(initialUrlState.pipelines);
  const [selectedSegments, setSelectedSegments] = useState<string[]>(initialUrlState.segments);
  const [camera, setCamera] = useState<MapCamera | undefined>(initialUrlState.camera);

  const pipelineRegions = useMemo(() => {
    const collect = (paths: string[] | undefined, into: Set<string>) => {
      for (const path of paths ?? [])
        for (const el of path.split(',')) {
          const token = el.trim();
          if (/^region:/i.test(token)) into.add(token.toLowerCase());
        }
    };
    const map = new Map<string, Set<string>>();
    for (const pipeline of pipelineNodes) {
      const regions = new Set<string>();
      collect(pipeline.paths, regions);
      for (const segment of pipeline.segments ?? []) collect(segment.paths, regions);
      for (const region of pipeline.regions ?? [])
        for (const zone of region.zones ?? [])
          for (const asset of zone.assets ?? []) collect(asset.paths, regions);
      map.set(pipeline._id, regions);
    }
    return map;
  }, [pipelineNodes]);

  const regionOptions = useMemo(
    () =>
      regions.features.map(f => ({
        id: f.properties?.id as string,
        name: f.properties?.name as string,
      })),
    [regions],
  );

  const regionScopedNodes = useMemo(() => {
    if (!selectedRegions.length) return pipelineNodes;
    const wanted = new Set(selectedRegions.map(r => r.toLowerCase()));
    return pipelineNodes.filter(n => {
      const regions = pipelineRegions.get(n._id);
      return !!regions && [...regions].some(r => wanted.has(r));
    });
  }, [pipelineNodes, pipelineRegions, selectedRegions]);

  const pipelineOptions = useMemo(
    () => regionScopedNodes.map(n => ({ id: n._id, name: n.name })),
    [regionScopedNodes],
  );

  const pipelineScopedNodes = useMemo(() => {
    if (!selectedPipelines.length) return regionScopedNodes;
    const wanted = new Set(selectedPipelines);
    return regionScopedNodes.filter(n => wanted.has(n._id));
  }, [regionScopedNodes, selectedPipelines]);

  const segmentOptions = useMemo(
    () =>
      pipelineScopedNodes.flatMap(p =>
        p.segments.map(s => ({ id: s._id, name: `${p.name}, ${s.name}` })),
      ),
    [pipelineScopedNodes],
  );

  const visibleNodes = useMemo(() => {
    if (!selectedSegments.length) return pipelineScopedNodes;
    const wanted = new Set(selectedSegments);
    return pipelineScopedNodes
      .map(n => ({ ...n, segments: n.segments.filter(s => wanted.has(s._id)) }))
      .filter(n => n.segments.length > 0);
  }, [pipelineScopedNodes, selectedSegments]);

  const visibleRegions = useMemo(() => {
    if (!selectedRegions.length) return regions;
    const wanted = new Set(selectedRegions);
    return {
      ...regions,
      features: regions.features.filter(f => wanted.has(f.properties?.id as string)),
    };
  }, [regions, selectedRegions]);

  // Drop selections that fall out of scope as parent filters narrow.
  useEffect(() => {
    if (!pipelineNodes.length) return;
    const valid = new Set(regionScopedNodes.map(n => n._id));
    setSelectedPipelines(prev => {
      const next = prev.filter(id => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [pipelineNodes, regionScopedNodes]);

  useEffect(() => {
    if (!pipelineNodes.length) return;
    const valid = new Set(pipelineScopedNodes.flatMap(p => p.segments.map(s => s._id)));
    setSelectedSegments(prev => {
      const next = prev.filter(id => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [pipelineNodes, pipelineScopedNodes]);

  const pipelines = useMemo(() => pipelineGraphToGeoJson(visibleNodes), [visibleNodes]);
  const zones = useMemo(
    () => pipelineGraphToZonesGeoJson(visibleNodes, noticesByEntity),
    [visibleNodes, noticesByEntity],
  );

  const urlState: MapUrlState = useMemo(
    () => ({
      regions: selectedRegions,
      pipelines: selectedPipelines,
      segments: selectedSegments,
      camera,
      zone: initialUrlState.zone,
    }),
    [selectedRegions, selectedPipelines, selectedSegments, camera, initialUrlState],
  );

  useCopyLinkAction(urlState);

  const zoneFocus = useMemo(() => {
    if (!initialUrlState.zone || initialUrlState.camera) return undefined;
    const feature = zones.features.find(f => f.properties?.id === initialUrlState.zone);
    if (!feature) return undefined;
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    return { center: [lng, lat] as [number, number], zoom: ZONE_FOCUS_ZOOM };
  }, [initialUrlState, zones]);

  const urlFocus = useMemo(
    () =>
      initialUrlState.camera
        ? { center: initialUrlState.camera.center, zoom: initialUrlState.camera.zoom }
        : undefined,
    [initialUrlState],
  );

  useEffect(() => {
    entityApi
      .getRegionEntities()
      .then(entities => {
        setRegions(entitiesToRegionsGeoJson(entities));
      })
      .catch(err => console.error('[map] Failed to load region entities', err));
  }, []);

  useEffect(() => {
    entityApi
      .getPipelineGraph()
      .then(async nodes => {
        setPipelineNodes(nodes);

        const entityKeys = collectMapEntityKeys(nodes);
        const sinceIso = dayjs().subtract(90, 'day').toISOString();
        const notices = await pipelineNotificationApi.getNoticesByEntities(entityKeys, sinceIso);

        const byEntity = new Map<string, unknown[]>();
        notices.forEach(n => {
          const display = {
            id: n._id,
            title: n.eventTypeClassification?.name || n.eventType,
            summary: `Planning: ${n.planningStatus} · ${n.eventStatus}`,
            date: dayjs(n.postedAt).format('YYYY-MM-DD'),
            postedAt: n.postedAt,
            url: n.sourceUrl,
          };
          (n.relatedEntities ?? []).forEach((a: any) => {
            const key = typeof a === 'string' ? a : a?._id;
            if (!key) return;
            const arr = byEntity.get(key) ?? [];
            arr.push(display);
            byEntity.set(key, arr);
          });
        });

        setNoticesByEntity(byEntity);
      })
      .catch(err => console.error('[map] Failed to load pipeline graph', err));
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <PipelineMap
          pipelines={pipelines}
          regions={visibleRegions}
          zones={zones}
          focus={urlFocus ?? zoneFocus}
          onCameraChange={setCamera}
          toolbar={
            <Row>
              <Col md={4} className={'pb-1'}>
                <MapFilterDropdown
                  label="Regions"
                  allLabel="All regions"
                  options={regionOptions}
                  selected={selectedRegions}
                  onChange={setSelectedRegions}
                />
              </Col>
              <Col md={4} className={'pb-1'}>
                <MapFilterDropdown
                  label="Pipelines"
                  allLabel="All pipelines"
                  options={pipelineOptions}
                  selected={selectedPipelines}
                  onChange={setSelectedPipelines}
                />
              </Col>
              <Col md={4} className={'pb-1'}>
                <MapFilterDropdown
                  label="Segments"
                  allLabel="All segments"
                  options={segmentOptions}
                  selected={selectedSegments}
                  onChange={setSelectedSegments}
                />
              </Col>
            </Row>
          }
        />
      </div>
    </div>
  );
}
